import { BehaviorSubject } from 'rxjs';
import { invariant } from '@streem/toolbox';
import { streemLogger } from '../util/streem_logger';
export class ChimeAudioTrack {
    constructor(audioVideoFacade, attendeeId, externalUserId, isRemote, audioLevelSubject, onMuted) {
        this.audioVideoFacade = audioVideoFacade;
        this.attendeeId = attendeeId;
        this.externalUserId = externalUserId;
        this.isRemote = isRemote;
        this.audioLevelSubject = audioLevelSubject;
        this.onMuted = onMuted;
        this.enabledSubject = new BehaviorSubject(true);
        this.startedSubject = new BehaviorSubject(false);
        this.logger = streemLogger.extend('sdk-chime-video:ChimeLocalAudioTrack');
        this.kind = 'audioTrack';
        this.enabled = this.enabledSubject.asObservable();
        this.started = this.startedSubject.asObservable();
        // This is a little bit of a hack.  IAudioTrack expects audioLevel to be an observable on
        //    the track, but the audio level for each participant is obtained via realtimeSubscribeToVolumeIndicator,
        //    which is established in the ChimeRoom.  So, the ChimeRoom creates audioLevelSubjects for each participant,
        //    and passes them to the ChimeAudioTrack when it creates the track so that it can fullly implement IAudioTrack.
        this.audioLevel = this.audioLevelSubject.asObservable();
    }
}
export class ChimeLocalAudioTrack extends ChimeAudioTrack {
    constructor(audioVideoFacade, attendeeId, externalUserId, audioLevelSubject) {
        super(audioVideoFacade, attendeeId, externalUserId, false, audioLevelSubject);
    }
    async disconnect() {
        this.logger.info(`ChimeLocalAudioTrack disconnecting the local audio track for userId=${this.externalUserId}`);
        await this.audioVideoFacade.stopAudioInput();
        this.audioLevelSubject.next(0);
        this.enabledSubject.next(false);
        this.startedSubject.next(false);
        this.trackLabel = '';
    }
    setEnabled(enable) {
        this.logger.info(`LocalChimeAudioTrack setEnabled called with enable: ${enable}`);
        if (enable) {
            this.audioVideoFacade.realtimeUnmuteLocalAudio();
        }
        else {
            this.audioVideoFacade.realtimeMuteLocalAudio();
        }
        this.enabledSubject.next(enable);
    }
    async restart(constraints) {
        this.logger.debug(`Restarting local audio for user with userId=${this.externalUserId}`, constraints ?? {});
        invariant(constraints, 'constraints must not be undefined');
        try {
            const audioMediaStream = await this.audioVideoFacade.startAudioInput(constraints);
            invariant(audioMediaStream, 'Failed to start audio input');
            this.startedSubject.next(true);
            this.trackLabel = audioMediaStream.getAudioTracks()[0].label;
            return true;
        }
        catch (error) {
            this.logger.error(`Failed to restart local audio for user with userId=${this.externalUserId}`, {
                errorMessage: error.message,
                errorStack: error.stack,
            });
            return false;
        }
    }
    async initializeAudioTrack(audioConstraints, audioInputDevices) {
        invariant(audioConstraints, 'audioConstraints must not be false or undefined');
        const inputDevice = audioConstraints === true
            ? await this.findDefaultMicrophone(audioInputDevices)
            : audioConstraints;
        this.logger.debug(`Starting local audio with inputDevice`, inputDevice);
        const audioMediaStream = await this.audioVideoFacade.startAudioInput(inputDevice);
        invariant(audioMediaStream, 'Failed to start audio input');
        this.startedSubject.next(true);
        const audioStreamTrack = audioMediaStream.getAudioTracks()[0];
        this.trackLabel = audioStreamTrack.label;
        this.logger.info(`LocalChimeAudioTrack with label: ${this.trackLabel} created local audio for user with userId=${this.externalUserId}`);
        return true;
    }
    async findDefaultMicrophone(audioInputDevices) {
        invariant(audioInputDevices.length, 'No audio input devices found');
        const defaultDevice = audioInputDevices.find(device => device.deviceId === 'default');
        if (defaultDevice) {
            return defaultDevice;
        }
        this.logger.warn('No default audio input device found, using first device');
        return audioInputDevices[0];
    }
}
// In Chime, there is only 1 remote audio track that comes from the Chime server.  It has all of the
//    other participants' audio tracks in it.
export class ChimeRemoteAudioTrack extends ChimeAudioTrack {
    constructor(audioVideoFacade, attendeeId, externalUserId, audioLevelSubject) {
        super(audioVideoFacade, attendeeId, externalUserId, true, audioLevelSubject, (muted) => {
            this.enabledSubject.next(!muted);
        });
        this.audioElement = document.createElement('audio');
        this.audioTrackSubject = new BehaviorSubject(this.audioElement);
        this.audioTrack = this.audioTrackSubject.asObservable();
    }
    async initializeAudioTrack(audioOutputDevices, remotePreferredAudioOutputDeviceId) {
        try {
            const initialOutputDevice = await this.findInitialAudioOutput(audioOutputDevices, remotePreferredAudioOutputDeviceId);
            if (initialOutputDevice) {
                await this.audioVideoFacade.chooseAudioOutput(initialOutputDevice.deviceId);
                this.trackLabel = initialOutputDevice.label;
            }
            this.logger.info(`Preferred audio output device:`, remotePreferredAudioOutputDeviceId);
            this.logger.info(`List of available audio output devices:`, JSON.stringify(audioOutputDevices));
            this.logger.info(`Selected audio output device for the call:`, JSON.stringify(initialOutputDevice));
            await this.audioVideoFacade.bindAudioElement(this.audioElement);
            // Story to determine if enabled should be set:
            //     https://streempro.atlassian.net/browse/ST-5241
            this.startedSubject.next(true);
            return true;
        }
        catch (error) {
            this.logger.error(`Failed to initialize audio track for user with userId=${this.externalUserId}:`, error);
            throw error;
        }
    }
    disconnect() {
        this.logger.info('RemoteChimeAudioTrack disconnecting the local audio track');
        this.audioVideoFacade.unbindAudioElement();
        this.audioLevelSubject.next(0);
        this.enabledSubject.next(false);
        this.startedSubject.next(false);
        this.audioTrackSubject.next(undefined);
        this.trackLabel = '';
    }
    selectSpeaker(deviceId, userId) {
        this.logger.debug(`selectSpeaker called for userId: ${userId} deviceId: ${deviceId}`);
        return this.audioVideoFacade.chooseAudioOutput(deviceId);
    }
    supportsSinkId() {
        // some browsers do not support setSinkId:
        //    https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/setSinkId
        // which is required by the Chime SDK to change the audio output device:
        //    https://aws.github.io/amazon-chime-sdk-js/modules/apioverview.html#2e-configure-the-audio-output-device-optional
        return typeof this.audioElement.setSinkId === 'function';
    }
    async findInitialAudioOutput(audioOutputDevices, remotePreferredAudioOutputDeviceId) {
        const supportsSinkId = this.supportsSinkId();
        // Note that some browsers do not return information about audio output devices, so the
        //     length of audioOutputDevices may be 0 even though there is a speaker available.
        //     In that case, we will use the default speaker.
        if (supportsSinkId && audioOutputDevices.length > 0) {
            const preferredAudioOutputDevice = audioOutputDevices.find(device => device.deviceId === remotePreferredAudioOutputDeviceId);
            if (preferredAudioOutputDevice) {
                return preferredAudioOutputDevice;
            }
            /* If the preferred audio output device id is set to 'default' it should be returned in the condition above
             * but because we are required to store this value on the chime room when the local track is created this
             * is a fallback in case the preferred output device is disconnected in the time between the creation of
             * the local track and the remote track.
             */
            const defaultDevice = audioOutputDevices.find(device => device.deviceId === 'default');
            if (defaultDevice) {
                return defaultDevice;
            }
            this.logger.info('No default audio output device found, using first device');
            return audioOutputDevices[0];
        }
        this.logger.info('No initial audio output could be found.  Browser may not support sinkId or no audio outputs were available', {
            userId: this.externalUserId,
            attendeeId: this.attendeeId,
            supportsSinkId,
            audioOutputDevicesLength: audioOutputDevices.length,
        });
        return undefined;
    }
}
//# sourceMappingURL=audio_tracks.js.map